/**
 * Whether a finding still describes the document in front of the reader.
 *
 * A finding's `affectedSections` name sections of the document as it stood when
 * the assessment was written, and its `requiredChanges` ask for what that text
 * was missing. Both are answers about one revision of one document. When the
 * document moves on — a replaced file, a captured text version — the answers do
 * not move with it: sections shift or vanish, and a required change may be
 * something the new text already does. The finding is not wrong, exactly; it is
 * about a document that no longer exists, which is worse, because nothing on the
 * page says so.
 *
 * This module is the one place that decides. It compares the revision the
 * assessment was grounded in against the document's revision now, and says how
 * far behind it is. It deliberately does not expose the revision numbers
 * themselves: `revision` counts both file replacements and text captures, so it
 * matches neither the file version nor the text version a reader sees in the
 * version history, and printing it would put a fourth number on a page that
 * already has three.
 *
 * @module findingStaleness
 */

/** How far a finding's assessment has fallen behind its document. */
export type FindingAssessmentAge =
  /** No revision was recorded — every row older than the column, and any assessment that never read the text. */
  | { readonly known: false }
  /** Recorded, and the document has not moved since. */
  | { readonly known: true; readonly stale: false }
  /** Recorded, and the document has moved `changesSince` times since. */
  | { readonly known: true; readonly stale: true; readonly changesSince: number };

const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/**
 * How far behind a finding's assessment is.
 *
 * Unknown wins over stale in every uncertain case: a missing stamp, a
 * nonsensical one, or a stamp *ahead* of the document. The last of those should
 * not happen — both writers bump under the document's row lock — but if it ever
 * does, the honest report is "we cannot tell", not a negative count or a
 * confident "current". A warning the reader cannot act on is noise; a warning
 * that is wrong costs more than the one it replaces.
 */
export function findingAssessmentAge(
  finding: FindingAssessmentProvenance,
): FindingAssessmentAge {
  const assessed = finding.assessedRevision;
  const current = finding.policyRevision;
  if (!isCount(assessed) || !isCount(current)) return { known: false };
  // Ahead of the document is not "current", it is nonsense: both writers bump
  // under the document's row lock, so it should not happen. Reporting it as
  // known-and-current would also stop it ever being looked at again, which is
  // the wrong way to fail — an unknown gets re-read, a false "current" does not.
  if (current < assessed) return { known: false };
  if (current === assessed) {
    // The revisions agree, but the file version may not — and during a rolling
    // deployment that is exactly what happens: an old instance replaces a file
    // without bumping `revision`, a new one stamps a finding against the
    // unchanged counter, and the finding claims to describe text it never read.
    // The file version is written by the old code too, so it still moves.
    const assessedFile = finding.assessedVersion;
    const currentFile = finding.currentVersion;
    if (isCount(assessedFile) && isCount(currentFile) && currentFile > assessedFile)
      return { known: true, stale: true, changesSince: currentFile - assessedFile };
    return { known: true, stale: false };
  }
  return { known: true, stale: true, changesSince: current - assessed };
}

/**
 * Worth re-reading: either we know the document has moved past the assessment,
 * or nobody recorded what it was assessed against at all.
 *
 * The unknown case is deliberately included, and it is the one that matters at
 * rollout: every finding that predates these columns has no stamp, so a rule
 * that only re-read *known*-stale findings would never re-read any of them, and
 * the feature would sit there waiting for a provenance it could only acquire by
 * running. Including unknowns costs one re-reading per document, after which
 * its findings are stamped and this is quiet again.
 *
 * Says nothing about status — the caller filters for open findings, because a
 * decided one is a record and is never re-read.
 */
export const findingProvenanceUncertain = (finding: FindingAssessmentProvenance): boolean => {
  const age = findingAssessmentAge(finding);
  return !age.known || age.stale;
};

/** True only when we know the document has moved on. Never true on an unknown. */
export const findingIsStale = (finding: {
  readonly assessedRevision?: number | null;
  readonly policyRevision?: number | null;
}): boolean => {
  const age = findingAssessmentAge(finding);
  return age.known && age.stale;
};

/** A finding, as much of it as any of this needs. */
export interface FindingAssessmentProvenance {
  readonly assessedRevision?: number | null;
  readonly policyRevision?: number | null;
  /** The document version the assessment was grounded in, as a reader sees it. */
  readonly assessedVersion?: number | null;
  /** The document version now. */
  readonly currentVersion?: number | null;
}

/**
 * The warning, in the words the version history already uses. Null when there
 * is nothing to warn about, so a caller can render this or nothing without
 * repeating the staleness test.
 *
 * Prefers the version numbers the reader knows — "assessed against v2 · now at
 * v4" — and falls back to counting the changes when those numbers cannot say
 * it. They cannot in two cases: a finding stamped before `assessedVersion`
 * existed, and a document whose movement was a *text* capture, which advances
 * `revision` without advancing the file version, so both versions read the
 * same while the document has genuinely moved. Counting is vaguer but never
 * wrong, and a warning that contradicts itself ("assessed against v4 · now at
 * v4") would be worse than a vague one.
 */
export function findingStalenessLabel(finding: FindingAssessmentProvenance): string | null {
  const age = findingAssessmentAge(finding);
  if (!age.known || !age.stale) return null;
  const assessed = finding.assessedVersion;
  const current = finding.currentVersion;
  if (isCount(assessed) && isCount(current) && current > assessed)
    return `Assessed against v${assessed} · now at v${current}`;
  return age.changesSince === 1
    ? "The document has changed once since this was assessed"
    : `The document has changed ${age.changesSince === 2 ? "twice" : `${age.changesSince} times`} since this was assessed`;
}

/**
 * The short form for a chip beside a status pill, where the sentence above does
 * not fit. Same test, same silence when there is nothing to say.
 */
export const findingStalenessChip = (finding: {
  readonly assessedRevision?: number | null;
  readonly policyRevision?: number | null;
}): string | null => (findingIsStale(finding) ? "Document changed since" : null);

/** The file version an assessment read, and how many text versions existed then. */
export interface FindingAssessedBaseline {
  readonly fileVersion: number;
  readonly textVersions: number;
}

/**
 * What a finding's assessment was grounded in, split like with like: the file
 * version it read and how many text versions had been captured by then.
 *
 * A document's file versions and text versions number independently — both
 * start at 1, so "v2" can name either — which is why `assessedVersion` alone
 * cannot say whether a text capture came after the assessment. The two stamps
 * together can: `assessedRevision` is the file version plus the count of text
 * versions at that moment, and text versions are numbered 1, 2, 3… in capture
 * order and never deleted, so the count is the highest text version then.
 *
 * Null when the stamps are missing or inconsistent — every finding older than
 * them, and any assessment that never read the text. Callers treat a null as
 * "no baseline", never as "nothing is newer".
 */
export function findingAssessedBaseline(finding: {
  readonly assessedVersion?: number | null;
  readonly assessedRevision?: number | null;
}): FindingAssessedBaseline | null {
  const file = finding.assessedVersion;
  const revision = finding.assessedRevision;
  if (!isCount(file) || !isCount(revision) || file < 1 || revision < file) return null;
  return { fileVersion: file, textVersions: revision - file };
}

/** A version of a document as the resolution rules see it. */
export interface DocumentVersionStamp {
  /** File versions and text captures number independently; "v2" can name either. */
  readonly kind: "file" | "text";
  readonly version: number;
  /** When the file was uploaded, or the text captured. */
  readonly createdAt: string | Date;
}

const timeOf = (value: string | Date): number =>
  value instanceof Date ? value.getTime() : Date.parse(value);

/**
 * The versions of a document that carry content the assessment did not read.
 *
 * A file version does when it is newer than the file the assessment read. A
 * text version is only a snapshot of the current file's extracted text — a
 * publish or a capture of an unchanged file freezes the very text the finding
 * was raised about, under a new number — so a text version counts only when it
 * was captured after a newer file than the assessed one had been uploaded, and
 * is itself beyond the text versions the assessment had seen. With no newer
 * file, no text version counts. File and text numbers are never compared with
 * each other.
 */
export function versionsAfterAssessment<T extends DocumentVersionStamp>(
  versions: ReadonlyArray<T>,
  baseline: FindingAssessedBaseline,
): T[] {
  const newerFileTimes = versions
    .filter((row) => row.kind === "file" && row.version > baseline.fileVersion)
    .map((row) => timeOf(row.createdAt))
    .filter((time) => !Number.isNaN(time));
  const newerFileAt = newerFileTimes.length === 0 ? null : Math.min(...newerFileTimes);
  return versions.filter((row) =>
    row.kind === "file"
      ? row.version > baseline.fileVersion
      : newerFileAt !== null &&
        row.version > baseline.textVersions &&
        timeOf(row.createdAt) > newerFileAt,
  );
}
